import InsurerLogoImage from "@/shared/components/InsurerLogoImage";
import { closeModal } from "@/shared/redux/reducers/uiSlice";
import {
  formatAmountByConfig,
  hasEmbeddedCurrencyText,
  parseAmountValue,
  replaceEmbeddedAmountWithGrouped,
} from "@/shared/utils/amountFormat";
import { extractInsurerLogoFile } from "@/shared/utils/getInsurarLogo";
import { getCheckedQuoteAddOnItems, getQuoteDisplayTotal } from "@/shared/utils/quoteAddOns";
import { handleQuoteBuyNowNavigation } from "@/shared/utils/quoteNavigation";
import { getNormalizedQuoteSections } from "@/shared/utils/quoteSections";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { usePosterReducers } from "../../redux/store/useGetData";
import { Button } from "../Button";
import CurrencySymbol from "./CurrencySymbol";
import {
  getQuoteItemPriceValue,
  isPositiveNumberLike,
  shouldShowQuoteItem,
  shouldUseAmountForDisplay,
} from "./quoteDisplay";

type CompareQuoteProps = {
  onBuyNow?: (quote: any) => Promise<void> | void;
  isReadOnly?: boolean;
};

const isBrandLogoNeedingTighterFit = (name?: string) =>
  /insurehub|nexinsure|nexaccel/i.test(String(name || ""));

const formatCompareValue = (
  value: unknown
): { numericValue: number | null; displayValue: string; hasEmbeddedCurrency: boolean } => {
  const parsedNumber = parseAmountValue(value);
  if (parsedNumber === null) {
    return {
      numericValue: null,
      displayValue: value === null || value === undefined ? "-" : String(value),
      hasEmbeddedCurrency: false,
    };
  }

  const formattedNumber = formatAmountByConfig(parsedNumber, {
    minimumFractionDigits: 0,
    maximumFractionDigits: Math.abs(parsedNumber % 1) > Number.EPSILON ? 2 : 0,
    fallback: "0",
  });

  if (typeof value === "string" && hasEmbeddedCurrencyText(value)) {
    return {
      numericValue: parsedNumber,
      displayValue: replaceEmbeddedAmountWithGrouped(value.trim(), {
        minimumFractionDigits: 0,
        maximumFractionDigits: Math.abs(parsedNumber % 1) > Number.EPSILON ? 2 : 0,
      }),
      hasEmbeddedCurrency: true,
    };
  }

  return {
    numericValue: parsedNumber,
    displayValue: formattedNumber,
    hasEmbeddedCurrency: false,
  };
};

const CompareQuote = ({ onBuyNow, isReadOnly = false }: CompareQuoteProps) => {
  const {
    commonSlice: { quoteList },
  } = usePosterReducers();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [buyingQuoteKey, setBuyingQuoteKey] = useState<string | null>(null);

  const compareQuote = quoteList.filter((quote) => quote.isCompare);
  const isAddOnSection = (title?: string) =>
    ["add-ons", "add ons", "addons"].includes(
      String(title || "")
        .trim()
        .toLowerCase()
    );
  const shouldShowItemInCompare = (
    item?: {
      is_checked?: boolean;
      label?: unknown;
      value?: unknown;
      amount?: unknown;
      premium?: unknown;
    },
    sectionTitle?: string
  ) => {
    if (isAddOnSection(sectionTitle)) {
      return Boolean(item?.is_checked) || shouldShowQuoteItem(item);
    }

    return shouldShowQuoteItem(item);
  };
  const getQuoteKey = (quote: any) =>
    `${quote?.insurer?.name || ""}_${quote?.plan?.id || ""}_${quote?.plan?.name || ""}_${quote?.plan?.price?.amount || ""}`;
  const normalizedCompareQuotes = compareQuote.map((quote) => {
    const selectedAddOns = getCheckedQuoteAddOnItems(quote);
    console.log("selectedAddOns::", selectedAddOns);
    const sections = getNormalizedQuoteSections(quote?.sections || []);

    return {
      sections: selectedAddOns?.length
        ? [
            ...sections,
            {
              title: "Add-ons",
              items: selectedAddOns,
            },
          ]
        : sections,
    };
  });
  const visibleSections = Array.from(
    new Set(
      normalizedCompareQuotes?.flatMap((item) => item.sections.map((section) => section.title))
    )
  );

  function getCoverageDisplay(coverage, sectionTitle?: string) {
    if (!shouldShowItemInCompare(coverage, sectionTitle)) {
      return "❌";
    }

    if (coverage?.value === true && !isPositiveNumberLike(coverage?.amount)) {
      return (
        <span className="inline-flex items-center rounded-full bg-theme-success-bright-soft px-3 py-1 text-xs font-medium text-theme-success-text-strong">
          ✓ Included
        </span>
      );
    }

    const rawValue =
      shouldUseAmountForDisplay(coverage) || isPositiveNumberLike(getQuoteItemPriceValue(coverage))
        ? getQuoteItemPriceValue(coverage)
        : coverage?.value;

    const { numericValue, displayValue, hasEmbeddedCurrency } = formatCompareValue(rawValue);

    return (
      <>
        {coverage?.is_checked && (
          <div className="flex justify-center items-center mb-1 text-xs">
            <p className="bg-theme-success-bright-soft rounded-full w-fit px-2 py-1">✓ Selected</p>
          </div>
        )}

        <div className="flex items-center justify-center gap-1">
          {!hasEmbeddedCurrency && (
            <CurrencySymbol currency={coverage?.currency} value={numericValue} />
          )}
          <span>{displayValue}</span>
        </div>
      </>
    );
  }

  const callBuyNow = async (data) => {
    if (isReadOnly) {
      return;
    }

    setBuyingQuoteKey(getQuoteKey(data));
    dispatch(closeModal());

    try {
      if (onBuyNow) {
        await Promise.resolve(onBuyNow(data));
        return;
      }

      handleQuoteBuyNowNavigation(navigate, data);
    } finally {
      setBuyingQuoteKey(null);
    }
  };

  return (
    <div className="relative h-[70vh] overflow-y-auto thin-scrollbar border border-theme-border rounded-lg">
      <table className="min-w-full border-collapse text-sm">
        <thead className="bg-theme-surface">
          <tr>
            {/* Coverage column */}
            <th
              className="
        sticky top-0 left-0 z-40
        bg-theme-surface
        px-5 py-4
        text-left text-sm font-semibold text-theme-text
        border-b border-theme-border
      "
            >
              Coverage
            </th>

            {compareQuote.map((item, idx) => (
              <th
                key={idx}
                className="
          sticky top-0 z-30
          bg-theme-surface
          px-5 py-2
          text-center
          border-b border-theme-border min-w-40
        "
              >
                <div className="flex h-28 items-center justify-center">
                  <InsurerLogoImage
                    insurerName={item?.insurer?.name}
                    insurerLogoFile={extractInsurerLogoFile(item?.insurer)}
                    alt={item?.insurer?.name}
                    className={`w-auto object-contain ${
                      isBrandLogoNeedingTighterFit(item?.insurer?.name)
                        ? "max-h-16 max-w-36"
                        : "max-h-20 max-w-44"
                    }`}
                  />
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {visibleSections?.map((sectionTitle) => {
            const sectionItems = Array.from(
              new Set(
                normalizedCompareQuotes?.flatMap(
                  ({ sections }) =>
                    sections
                      ?.find((s) => s.title === sectionTitle)
                      ?.items?.filter((item) => shouldShowItemInCompare(item, sectionTitle))
                      ?.map((item) => item.label) || []
                )
              )
            );

            if (sectionItems.length === 0) {
              return null;
            }

            return (
              <React.Fragment key={sectionTitle}>
                {/* ================= SECTION HEADER ================= */}
                <tr className="border border-theme-surface-muted">
                  <td
                    colSpan={compareQuote.length + 1}
                    className="bg-theme-surface-muted px-6 py-4"
                  >
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-theme-primary" />
                      <span className="text-sm font-semibold text-theme-text uppercase tracking-wide">
                        {sectionTitle}
                      </span>
                    </div>
                  </td>
                </tr>

                {/* ================= SECTION ROWS ================= */}
                {sectionItems.map((label, rowIdx) => (
                  <tr
                    key={label}
                    className={`border border-theme-border ${
                      rowIdx % 2 === 0 ? "bg-theme-surface" : "bg-theme-surface-muted"
                    }`}
                  >
                    {/* Coverage label */}
                    <td className="sticky left-0 bg-inherit px-10 py-3 text-sm text-theme-text-muted">
                      {label}
                    </td>

                    {/* Values */}
                    {normalizedCompareQuotes.map(({ sections }, idx) => {
                      const section = sections?.find((s) => s.title === sectionTitle);
                      const coverage = section?.items.find((it) => it.label === label);

                      return (
                        <td key={idx} className="px-5 py-3 text-center">
                          <span className="text-sm text-theme-text-soft">
                            {getCoverageDisplay(coverage, sectionTitle)}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>

        <thead className="bg-theme-accent-soft">
          <tr>
            <th
              className="
        sticky bottom-0 left-0 z-50
        bg-theme-accent-soft
        px-5 py-4
        text-left text-sm font-semibold text-theme-text
        border-t border-theme-border-strong
      "
            ></th>

            {compareQuote.map((item, idx) => (
              <th
                key={idx}
                className="
          sticky bottom-0 z-40
          bg-theme-accent-soft
          px-5 py-4
          text-center text-sm font-semibold text-theme-text
          border-t border-theme-border-strong
        "
              >
                <div className="flex flex-col items-center gap-2">
                  <span className="text-xs text-theme-text-soft">{item?.plan?.name}</span>

                  <div className="flex items-center gap-1 text-lg font-medium text-theme-primary">
                    <CurrencySymbol
                      currency={item.plan?.price?.currency}
                      value={parseAmountValue(getQuoteDisplayTotal(item))}
                    />
                    {formatCompareValue(getQuoteDisplayTotal(item)).displayValue}
                  </div>

                  <Button
                    onClick={() => callBuyNow(item)}
                    isLoading={buyingQuoteKey === getQuoteKey(item)}
                    disabled={isReadOnly}
                    loadingText="Processing..."
                  >
                    Buy Now
                  </Button>
                </div>
              </th>
            ))}
          </tr>
        </thead>
      </table>
    </div>
  );
};

export default CompareQuote;
